import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getTasks, deleteTask as deleteTaskAPI, updateTask as updateTaskAPI } from '@/lib/taskClient';
import {useClerk} from "@clerk/clerk-react";

interface Task {
  id: string;
  title: string;
  description: string;
  priority: "high" | "medium" | "low" | "urgent";
  dueTime: string;
  category: string;
  assignee?: string;
  progress: number;
  details: string;
  relatedCase?: string;
  tags?: string[];
  estimatedHours?: string;
  notes?: string;
  type?: string; // Добавлено
  createdAt: Date;
  timestamp: Date;
}

interface TaskManagerContextType {
  tasks: Task[];
  addTask: (task: Task) => Promise<void>;
  updateTask: (id: string, updates: Partial<Task>) => void;
  deleteTask: (id: string) => void;
  loading: boolean;
  error: string | null;
  refreshTasks: () => Promise<void>;
}

const TaskManagerContext = createContext<TaskManagerContextType | undefined>(undefined);

export function TaskManagerProvider({ children }: { children: ReactNode }) {
  const clerkUserId = useClerk().user.id
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedTasks = await getTasks(clerkUserId, clerkUserId);

      const formattedTasks = fetchedTasks.map(task => ({
        id: task.id,
        title: task.title,
        description: task.description || '',
        priority: task.priority as "high" | "medium" | "low" | "urgent",
        dueTime: task.due_time || 'No due date',
        category: task.category || 'General',
        assignee: task.assignee,
        progress: task.progress,
        details: task.details || '',
        relatedCase: task.related_case,
        tags: task.tags || [],
        estimatedHours: task.estimated_hours?.toString(),
        notes: task.notes,
        createdAt: new Date(task.created_at),
        timestamp: new Date(task.created_at)
      }));

      setTasks(formattedTasks);
      console.log('Loaded tasks from database:', formattedTasks);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const addTask = async (task: Task) => {
    console.log('Adding new task to TaskManager:', task);
    await loadTasks();
  };

  const updateTask = async (id: string, updates: Partial<Task>) => {
    try {
      await updateTaskAPI(id, {
        title: updates.title,
        description: updates.description,
        priority: updates.priority,
        due_time: updates.dueTime,
        category: updates.category,
        assignee: updates.assignee,
        progress: updates.progress,
        details: updates.details,
        notes: updates.notes,
        related_case: updates.relatedCase,
        tags: updates.tags,
        estimated_hours: updates.estimatedHours ? parseFloat(updates.estimatedHours) : undefined
      }, clerkUserId);

      setTasks(prev => prev.map(task =>
          task.id === id ? { ...task, ...updates } : task
      ));

      console.log('Task updated successfully:', id);
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  };

  const deleteTask = async (id: string) => {
    try {
      await deleteTaskAPI(id);

      setTasks(prev => prev.filter(task => task.id !== id));

      console.log('Task deleted successfully:', id);
    } catch (err) {
      console.error('Error deleting task:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const refreshTasks = async () => {
    await loadTasks();
  };

  return (
      <TaskManagerContext.Provider value={{
        tasks,
        addTask,
        updateTask,
        deleteTask,
        loading,
        error,
        refreshTasks
      }}>
        {children}
      </TaskManagerContext.Provider>
  );
}

export const useTaskManager = () => {
  const context = useContext(TaskManagerContext);
  if (context === undefined) {
    throw new Error('useTaskManager must be used within a TaskManagerProvider');
  }
  return context;
};